import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardBloomberg from './DashboardBloomberg';
import AdminDashboardBloomberg from './AdminDashboardBloomberg';
import ConsolidatedDashboardBloomberg from './ConsolidatedDashboardBloomberg';
import PortfolioOverviewDashboard from './admin/PortfolioOverviewDashboard';
import '../styles/bloomberg-theme.css';

type ViewType = 'overview' | 'individual' | 'consolidated';

const AdminNavigationBloomberg: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentView, setCurrentView] = React.useState<ViewType>('overview');
  const [selectedUserId, setSelectedUserId] = React.useState<string | undefined>();

  const routeTabs = [
    { path: '/bot', label: 'BOT' },
    { path: '/settings', label: 'SETTINGS' },
    { path: '/deposit', label: 'DEPOSIT' }
  ];

  const navItems = [
    {
      id: 'overview' as ViewType,
      name: 'PORTFOLIO OVERVIEW',
      description: 'MULTI-WALLET PERFORMANCE DASHBOARD'
    },
    {
      id: 'individual' as ViewType,
      name: 'INDIVIDUAL WALLETS',
      description: 'VIEW INDIVIDUAL USER WALLETS'
    },
    {
      id: 'consolidated' as ViewType,
      name: 'CONSOLIDATED VIEW',
      description: 'ALL WALLETS AGGREGATED'
    }
  ];

  const handleViewWallet = (userId: string) => {
    setSelectedUserId(userId);
    setCurrentView('individual');
  };

  return (
    <div className="bloomberg-terminal min-h-screen" style={{ backgroundColor: 'var(--bb-black)' }}>
      {/* Terminal Header */}
      <div className="bb-header" style={{ borderBottom: '2px solid var(--bb-amber)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <span className="bb-header-title">GRID TERMINAL</span>
          <span style={{ color: 'var(--bb-gray)', fontSize: '11px' }}>
            {user?.email} {user?.is_admin && '[ADMIN]'}
          </span>
        </div>
        <div className="bb-header-info" style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <span>{new Date().toLocaleDateString()}</span>
          <span>{new Date().toLocaleTimeString()}</span>
          <button
            onClick={logout}
            className="bb-button"
            style={{ padding: '4px 12px', fontSize: '11px' }}
          >
            LOGOUT
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        display: 'flex',
        backgroundColor: 'var(--bb-bg-secondary)',
        borderBottom: '1px solid var(--bb-border)'
      }}>
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentView(item.id)}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: currentView === item.id ? 'var(--bb-black)' : 'var(--bb-bg-secondary)',
              color: currentView === item.id ? 'var(--bb-amber)' : 'var(--bb-gray)',
              border: 'none',
              borderRight: '1px solid var(--bb-border)',
              borderTop: currentView === item.id ? '2px solid var(--bb-amber)' : '2px solid transparent',
              fontFamily: 'var(--bb-font-mono)',
              fontSize: '11px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontWeight: 'bold', letterSpacing: '0.1em', marginBottom: '4px' }}>
              {item.name}
            </div>
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              {item.description}
            </div>
          </button>
        ))}

        {/* Route-based tabs */}
        {routeTabs.map(routeTab => (
          <button
            key={routeTab.path}
            onClick={() => navigate(routeTab.path)}
            style={{
              padding: '12px 20px',
              backgroundColor: location.pathname === routeTab.path ? 'var(--bb-black)' : 'var(--bb-bg-secondary)',
              color: location.pathname === routeTab.path ? 'var(--bb-amber)' : 'var(--bb-gray)',
              border: 'none',
              borderRight: '1px solid var(--bb-border)',
              borderTop: location.pathname === routeTab.path ? '2px solid var(--bb-amber)' : '2px solid transparent',
              fontFamily: 'var(--bb-font-mono)',
              fontSize: '11px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ fontWeight: 'bold', letterSpacing: '0.1em' }}>
              {routeTab.label}
            </div>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div>
        {currentView === 'overview' && <PortfolioOverviewDashboard onViewWallet={handleViewWallet} />}
        {currentView === 'individual' && <AdminDashboardBloomberg preSelectedUserId={selectedUserId} />}
        {currentView === 'consolidated' && <ConsolidatedDashboardBloomberg />}
      </div>
    </div>
  );
};

export default AdminNavigationBloomberg;